import { type StateMachine } from "@pixelpuf/schemas/pxpf";
import { type AnyStateMachine, setup } from "xstate";
import { actionCatalog, actionCatalogIds } from "./catalogs/actions.ts";
import { guardCatalog, guardCatalogIds } from "./catalogs/guards.ts";

/**
 * Convert the flat `StateMachine` shape (initial + states + transitions[]) — our
 * persisted file-format representation — into an xstate `MachineConfig` (initial
 * + states.{id}.on.{event}: { target, guard?, actions? }).
 *
 * State ids are collected from `initial`, `transitions[].from|to`, and explicit
 * `states[].id`. Every collected id becomes a key in the output `states` map so
 * xstate can resolve transition targets even when no entry/exit metadata exists.
 *
 * Throws if the machine references a guard or action id missing from the catalogs,
 * or if any transition target is not a known state. Caller (the editor or codec
 * load) catches and surfaces a UI error while retaining the previous valid machine.
 */

interface BuildIssues {
  unknownGuards: string[];
  unknownActions: string[];
  danglingTargets: string[];
  missingInitial: boolean;
}

export class BuildError extends Error {
  readonly issues: BuildIssues;
  constructor(issues: BuildIssues) {
    const parts: string[] = [];
    if (issues.missingInitial) parts.push("initial state not in machine");
    if (issues.unknownGuards.length > 0) parts.push(`unknown guards: ${issues.unknownGuards.join(", ")}`);
    if (issues.unknownActions.length > 0) parts.push(`unknown actions: ${issues.unknownActions.join(", ")}`);
    if (issues.danglingTargets.length > 0) parts.push(`dangling targets: ${issues.danglingTargets.join(", ")}`);
    super(`buildUserMachine: ${parts.join("; ") || "unknown error"}`);
    this.issues = issues;
  }
}

function collectIssues(machine: StateMachine): BuildIssues {
  // "Declared" set excludes the `initial` field itself — checking initial
  // against a set that auto-includes it would never fire. A state is declared
  // if it appears in `states[]` OR as a transition endpoint.
  const declaredIds = new Set<string>();
  if (machine.states) {
    for (const s of machine.states) declaredIds.add(s.id);
  }
  for (const t of machine.transitions) {
    declaredIds.add(t.from);
    declaredIds.add(t.to);
  }

  const unknownGuards = new Set<string>();
  const unknownActions = new Set<string>();
  const danglingTargets = new Set<string>();

  for (const t of machine.transitions) {
    if (!declaredIds.has(t.to)) danglingTargets.add(t.to);
    if (t.guard && !guardCatalogIds.has(t.guard.id)) unknownGuards.add(t.guard.id);
    if (t.actions) {
      for (const a of t.actions) {
        if (!actionCatalogIds.has(a.id)) unknownActions.add(a.id);
      }
    }
  }
  if (machine.states) {
    for (const s of machine.states) {
      if (s.entry) {
        for (const a of s.entry) {
          if (!actionCatalogIds.has(a.id)) unknownActions.add(a.id);
        }
      }
      if (s.exit) {
        for (const a of s.exit) {
          if (!actionCatalogIds.has(a.id)) unknownActions.add(a.id);
        }
      }
    }
  }

  return {
    unknownGuards: [...unknownGuards],
    unknownActions: [...unknownActions],
    danglingTargets: [...danglingTargets],
    missingInitial: machine.initial === "" || !declaredIds.has(machine.initial),
  };
}

function hasIssues(i: BuildIssues): boolean {
  return i.missingInitial
    || i.unknownGuards.length > 0
    || i.unknownActions.length > 0
    || i.danglingTargets.length > 0;
}

interface XStateTransition {
  target: string;
  guard?: { type: string; params?: unknown };
  actions?: { type: string; params?: unknown }[];
}

interface XStateNode {
  on?: Record<string, XStateTransition>;
  entry?: { type: string; params?: unknown }[];
  exit?: { type: string; params?: unknown }[];
}

interface XStateConfig {
  id: string;
  initial: string;
  states: Record<string, XStateNode>;
}

/**
 * Pure converter: flat `StateMachine` → xstate-shaped config object.
 * Exported separately from `buildUserMachine` so tests + validators can
 * exercise the conversion without needing the catalogs.
 */
export function toXStateConfig(machine: StateMachine): XStateConfig {
  const issues = collectIssues(machine);
  if (hasIssues(issues)) throw new BuildError(issues);

  const states: Record<string, XStateNode> = {};
  const allIds = new Set<string>([machine.initial]);
  for (const t of machine.transitions) {
    allIds.add(t.from);
    allIds.add(t.to);
  }
  if (machine.states) { for (const s of machine.states) allIds.add(s.id); }

  for (const id of allIds) {
    states[id] = { on: {} };
  }

  if (machine.states) {
    for (const s of machine.states) {
      const node = states[s.id];
      if (node === undefined) continue;
      if (s.entry) node.entry = s.entry.map((a) => ({ type: a.id, params: a.params }));
      if (s.exit) node.exit = s.exit.map((a) => ({ type: a.id, params: a.params }));
    }
  }

  for (const t of machine.transitions) {
    const node = states[t.from];
    if (node === undefined) continue;
    const xs: XStateTransition = { target: t.to };
    if (t.guard) xs.guard = { type: t.guard.id, params: t.guard.params };
    if (t.actions) xs.actions = t.actions.map((a) => ({ type: a.id, params: a.params }));
    node.on ??= {};
    // Last writer wins on event collisions — collide is a soft warn handled by validate.ts.
    node.on[t.event] = xs;
  }

  return { id: "user", initial: machine.initial, states };
}

/**
 * Build a runtime xstate machine from a user-defined `StateMachine`. Throws
 * `BuildError` (with structured `issues`) on catalog miss or dangling target.
 */
export function buildUserMachine(machine: StateMachine): AnyStateMachine {
  const config = toXStateConfig(machine);
  return setup({
    types: {},
    guards: guardCatalog,
    actions: actionCatalog,
  }).createMachine(config);
}
